import axios from 'axios';
import { API_BASE_URL } from '../api';

const KEY = 'tc_session';
let installed = false;
let unauthorizedHandler = null;

// The app registers what to do on a 401 (log out). Safe to call at any time.
export function setUnauthorizedHandler(fn) { unauthorizedHandler = fn; }
const notifyUnauthorized = () => { if (unauthorizedHandler) unauthorizedHandler(); };

export function loadSession() {
  try {
    const s = JSON.parse(localStorage.getItem(KEY) || 'null');
    if (!s || !s.token || !s.username) return null;
    if (s.expiresAt && s.expiresAt <= Date.now()) { localStorage.removeItem(KEY); return null; }
    return s;
  } catch { return null; }
}

export function saveSession(user) {
  const session = {
    username: user.username, isAdmin: Boolean(user.isAdmin), role: user.role,
    mustChangePassword: Boolean(user.mustChangePassword), token: user.token, expiresAt: user.expiresAt,
  };
  try { localStorage.setItem(KEY, JSON.stringify(session)); } catch { /* ignore */ }
  return session;
}

export function markPasswordChanged() {
  const s = loadSession();
  if (!s) return null;
  return saveSession({ ...s, mustChangePassword: false });
}

export function clearSession() {
  try { localStorage.removeItem(KEY); } catch { /* ignore */ }
}

const currentToken = () => {
  const s = loadSession();
  return s ? s.token : null;
};

// Attach the bearer token to every request to our API (axios and fetch), and
// announce a 401 on an authenticated call so the app can drop back to the login screen.
// This MUST run at module load (see the bottom of the file): React runs child effects (data
// loading) before parent effects, so installing from a component effect left the first requests
// after a page refresh without a token.
export function installAuth() {
  if (installed) return;
  installed = true;

  axios.interceptors.request.use((config) => {
    const token = currentToken();
    if (token && String(config.url || '').startsWith(API_BASE_URL || '')) {
      config.headers = { ...config.headers, Authorization: `Bearer ${token}` };
    }
    return config;
  });
  axios.interceptors.response.use(
    (res) => res,
    (err) => {
      if (err.response && err.response.status === 401 && currentToken()) notifyUnauthorized();
      return Promise.reject(err);
    }
  );

  const originalFetch = window.fetch.bind(window);
  window.fetch = async (input, init = {}) => {
    const url = typeof input === 'string' ? input : input.url;
    const token = currentToken();
    const isApi = API_BASE_URL ? url.startsWith(API_BASE_URL) : url.startsWith('/');
    if (!token || !isApi) return originalFetch(input, init);
    const response = await originalFetch(input, { ...init, headers: { ...(init.headers || {}), Authorization: `Bearer ${token}` } });
    if (response.status === 401) notifyUnauthorized();
    return response;
  };
}

installAuth();
